import {
  Modal,
  Table,
  Progress,
  Statistic,
  Row,
  Col,
  Alert,
  Skeleton,
  Result,
  Button,
  Divider,
} from "antd";
import { useEffect, useReducer, useState, useContext } from "react";
import { createDelivery } from "@DELIVERIES";
import { mapProductsForApi } from "@HELPERS";
import { useHistory } from "react-router-dom";
import { exportExcel } from "@HELPERS";
import { AuthContext } from "@CONTEXTS";

const initialState = [];
const reducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case "SET_DELIVERIES":
      return payload.map((delivery) => ({
        ...delivery,
        status: "RUNNING",
      }));
    case "UPDATE_DELIVERY_STATUS_SUCCESS":
      return state.map((delivery) => {
        if (delivery.customerDeliveryNo === payload.customerDeliveryNo) {
          return {
            ...delivery,
            status: "SUCCESS",
          };
        }
        return delivery;
      });
    case "UPDATE_DELIVERY_STATUS_ERROR":
      return state.map((delivery) => {
        if (delivery.customerDeliveryNo === payload.customerDeliveryNo) {
          return {
            ...delivery,
            status: "ERROR",
            errorMessage: payload.errorMessage,
          };
        }
        return delivery;
      });
    default:
      break;
  }
};

const CreateResultModal = ({ onClose, data, onSuccessAndRemove }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const history = useHistory();
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [waitingCount, setWaitingCount] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allSuccess, setAllSuccess] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const { info } = useContext(AuthContext);

  const columns = [
    {
      title: "Sipariş numarası",
      dataIndex: "customerDeliveryNo",
      key: "customerDeliveryNo",
      width: "25%",
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      width: "50%",
      render: (data, record) => {
        switch (data) {
          case "RUNNING":
            return <Skeleton.Input active block shape="round" size="small" />;
          case "SUCCESS":
            return <Progress percent={100} size="small" />;
          case "ERROR":
            return (
              <Alert message={record.errorMessage} type="error" showIcon />
            );
          default:
            return "HATA!";
        }
      },
    },
  ];

  useEffect(() => {
    dispatch({ type: "SET_DELIVERIES", payload: data });
  }, [data, dispatch]);

  useEffect(() => {
    if (state) {
      const success = state?.filter(
        (delivery) => delivery.status === "SUCCESS"
      );
      const error = state?.filter((delivery) => delivery.status === "ERROR");
      const waiting = state?.filter(
        (delivery) => delivery.status === "RUNNING"
      );

      if (success.length === data.length) {
        setAllSuccess(true);
      }

      setSuccessCount(success.length);
      setErrorCount(error.length);
      setWaitingCount(waiting.length);
    }
  }, [data, state, dispatch]);

  useEffect(() => {
    if (allDone) {
      const doneDeliveries = [];
      const errorDeliveries = [];
      state.forEach((item, index) => {
        if (item.status === "SUCCESS") {
          doneDeliveries.push(item.customerDeliveryNo);
        } else {
          errorDeliveries.push({
            customerDeliveryNo: item.customerDeliveryNo,
            errorMessage: item.errorMessage,
          });
        }
      });

      if (typeof onSuccessAndRemove === "function") {
        onSuccessAndRemove(doneDeliveries);
      }

      if (errorDeliveries.length > 0) {
        setErrorList(errorDeliveries);
      }
    }
  }, [allDone, state]);

  const packageResolver = async (item) => {
    const formData = new FormData();

    formData.append("customerDeliveryNo", item.customerDeliveryNo || null);

    formData.append("warehouse", item.warehouse || null);
    formData.append("desi", item.desi || null);
    formData.append("manuelDesi", item.manuelDesi || null);
    formData.append("totalParcels", item.totalParcels || null);
    formData.append("deliveryType", item.deliveryType || null);
    if (item.slot) formData.append("slot", item.slot || null);
    formData.append("deliveryAmount", item.deliveryAmount || null);
    formData.append("paymentType", item.paymentType || null);
    formData.append("products", mapProductsForApi(item?.products));

    formData.append(
      "recipient",
      JSON.stringify({
        firstName: item.recipientName,
        lastName: item.recipientSurname,
        phone: item.recipientPhone.replace(/ /g, ""),
        address: {
          addressLine: item.recipientAddressLine,
          city: {
            id: item.recipientCity?.id,
            name: item.recipientCity.name,
          },
          town: {
            id: item.recipientTown?.id,
            name: item.recipientTown.name,
          },
          district: item.recipientDistrict
            ? {
                id: item.recipientDistrict?.id,
                name: item.recipientDistrict.name,
              }
            : null,
        },
      }) || null
    );

    if (item.receiverName) {
      formData.append(
        "receiver",
        JSON.stringify({
          firstName: item.receiverName,
          lastName: item.receiverSurname,
          phone: item.receiverPhone.replace(/ /g, ""),
        }) || null
      );
    }

    if (info?.customFields) {
      const customFields = info?.customFields?.filter((x) =>
        x?.deliveryType?.includes("DELIVERY")
      );

      const cfx = [];

      for (let i = 0; i < customFields.length; i++) {
        const customItem = customFields[i];

        cfx.push({
          name: customItem.name,
          value: item[customItem.name],
        });

        if (customItem.type === "FILE_UPLOADER") {
          if (
            (!item[customItem.name] ||
              item[customItem.name]?.length < 1 ||
              !item[customItem.name]?.[0]?.originFileObj) &&
            customItem.required
          ) {
            return new Promise((resolve) =>
              resolve({
                success: false,
                message: `${customItem.label} için geçersiz dosya.`,
              })
            );
          }

          formData.append(
            customItem.name,
            item[customItem.name]?.[0]?.originFileObj
          );
        }
      }

      if (cfx.length > 0) {
        formData.append("customFields", JSON.stringify(cfx));
      }
    }

    return new Promise((resolve) => {
      createDelivery(formData).then((result) => {
        if (result?.response?.status === 400) {
          resolve({
            success: false,
            message:
              result.response?.data?.message ||
              result?.response?.data?.error?.msg,
          });
        } else {
          resolve({ success: true });
        }
      });
    });
  };

  const packageDoNextPromise = (index) => {
    packageResolver(state[index]).then((result) => {
      if (result.success) {
        dispatch({
          type: "UPDATE_DELIVERY_STATUS_SUCCESS",
          payload: { customerDeliveryNo: state[index].customerDeliveryNo },
        });
      } else {
        dispatch({
          type: "UPDATE_DELIVERY_STATUS_ERROR",
          payload: {
            customerDeliveryNo: state[index].customerDeliveryNo,
            errorMessage: result.message,
          },
        });
      }
      index++;
      if (index < state.length) {
        packageDoNextPromise(index);
      } else {
        setLoading(false);
        setAllDone(true);
      }
    });
  };

  const downloadErrorDeliveries = () => {
    exportExcel(
      "Hatalı Gönderiler",
      [
        { title: "Gönderi Numarası", key: "customerDeliveryNo" },
        { title: "Hata Sebebi", key: "errorMessage" },
      ],
      errorList
    );
  };

  useEffect(() => {
    if (state && state.length > 0 && !mounted) {
      packageDoNextPromise(0);
      setMounted(true);
      setLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <Modal
      visible
      title="Toplu Gönderi Oluşturma Durumu"
      onCancel={() => {
        if (!loading) {
          onClose();
        }
      }}
      width={950}
      footer={null}
    >
      <Row gutter={24}>
        <Col span={8}>
          <Statistic title="Başarılı Gönderi Sayısı" value={successCount} />
        </Col>
        <Col span={8}>
          <Statistic title="Hatalı Gönderi Sayısı" value={errorCount} />
        </Col>
        <Col span={8}>
          <Statistic
            title="Sırada Bekleyen Gönderi Sayısı"
            value={waitingCount}
          />
        </Col>
      </Row>
      <div style={{ marginTop: 24 }}>
        {allSuccess ? (
          <>
            <Divider />
            <Result
              status="success"
              title="Gönderiler Başarıyla Oluşturuldu"
              subTitle={`Gönderiniz başarıyla oluşturuldu. Paketlenecek Gönderilerim sayfasından barkod yazdırma ve operasyona iletme işlemini yapabilirsiniz.`}
              extra={[
                <Button
                  type="primary"
                  key="console"
                  onClick={() => history.push("/deliveries/pre-order")}
                >
                  Paketlenecek Gönderilerim
                </Button>,
                <Button
                  key="buy"
                  onClick={() => {
                    onClose();
                  }}
                >
                  Kapat
                </Button>,
              ]}
            />
          </>
        ) : (
          <Table
            pagination={false}
            columns={columns}
            dataSource={state}
            scroll={{ y: 400 }}
            bordered
            rowKey="customerDeliveryNo"
          />
        )}
      </div>
    </Modal>
  );
};

export default CreateResultModal;
